import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { Button, Icon } from 'semantic-ui-react';
import {
  ButtonBack,
  ButtonNext,
  CarouselProvider,
  DotGroup,
  Slide,
  Slider,
} from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';

import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import Image from '@plone/volto/components/theme/Image/Image';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import {
  homepageImageSources,
  splitLines,
} from '../../../helpers/homepageUtils';

const isExternalHref = (href) =>
  href?.startsWith('http://') || href?.startsWith('https://');

const messages = defineMessages({
  previousSlide: {
    id: 'Previous slide',
    defaultMessage: 'Diapositiva anterior',
  },
  nextSlide: {
    id: 'Next slide',
    defaultMessage: 'Diapositiva següent',
  },
});

const SlideImage = ({ slide, priority = false }) => {
  const sources = homepageImageSources(slide.image, {
    minWidth: 800,
    fallback: 'large',
  });
  if (!sources.src) return null;

  return (
    <Image
      className="carousel-slide-image"
      src={sources.src}
      srcSet={sources.srcSet}
      sizes="100vw"
      alt={slide.title || ''}
      width={sources.width}
      height={sources.height}
      loading={priority ? 'eager' : 'lazy'}
      decoding={priority ? 'sync' : 'async'}
    />
  );
};

SlideImage.propTypes = {
  slide: PropTypes.object.isRequired,
  priority: PropTypes.bool,
};

const HomepageSliderBody = ({ slides = [], editPreview = false }) => {
  const intl = useIntl();
  const hero = homepageImageSources(slides[0]?.image, {
    minWidth: 800,
    fallback: 'large',
  });

  if (!slides.length) return null;

  return (
    <div className="carousel-container">
      {hero.src ? (
        <Helmet>
          {/* Volto renders Helmet meta tags before Helmet links, but its own
              viewport meta after them. Keep this intentional duplicate so the
              browser resolves imageSizes against the real viewport. */}
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link
            rel="preload"
            as="image"
            href={hero.src}
            imageSrcSet={hero.srcSet}
            imageSizes="100vw"
            fetchpriority="high"
          />
        </Helmet>
      ) : null}
      <CarouselProvider
        naturalSlideWidth={16}
        naturalSlideHeight={7}
        totalSlides={slides.length}
        isPlaying={slides.length > 0}
        interval={5000}
      >
        <Slider>
          {slides.map((slide, index) => (
            <Slide key={index} index={index}>
              <SlideImage slide={slide} priority={index === 0} />
              {slide.text_boto && (
                <div className="carousel-slide-button-container">
                  {editPreview || !slide.enllac ? (
                    <Button
                      className="carousel-slide-button"
                      content={slide.text_boto}
                      disabled={editPreview}
                    />
                  ) : (
                    <UniversalLink
                      href={slide.enllac}
                      openLinkInNewTab={isExternalHref(slide.enllac)}
                    >
                      <Button
                        as="span"
                        className="carousel-slide-button"
                        content={slide.text_boto}
                      />
                    </UniversalLink>
                  )}
                </div>
              )}
              <div className="carousel-slide-text-container">
                <div className="carousel-slide-title-container">
                  {splitLines(slide.title, 'carousel-slide-title')}
                </div>
                <div className="carousel-slide-subtitle-container">
                  {splitLines(slide.description, 'carousel-slide-subtitle')}
                </div>
              </div>
            </Slide>
          ))}
        </Slider>
        <div className="carousel-center-actions-container">
          <ButtonBack
            className="ui icon button"
            aria-label={intl.formatMessage(messages.previousSlide)}
          >
            <Icon name="chevron left" />
          </ButtonBack>
          <ButtonNext
            className="ui icon button"
            aria-label={intl.formatMessage(messages.nextSlide)}
          >
            <Icon name="chevron right" />
          </ButtonNext>
        </div>
        <div className="carousel-bottom-actions-container">
          <DotGroup />
        </div>
      </CarouselProvider>
    </div>
  );
};

HomepageSliderBody.propTypes = {
  slides: PropTypes.arrayOf(PropTypes.object),
  editPreview: PropTypes.bool,
};

export default HomepageSliderBody;
